/**
 * Streaming — the endpoints this organization's audit and runs are sent to: a
 * SIEM, a log pipeline, a Slack channel. An owner's and an administrator's page
 * (the Worker answers anybody else 404).
 *
 * Adding one shows its signing secret **once**, in a note to copy now; the page
 * never shows it again, nor the whole address (a Slack webhook's address is its
 * credential). Each endpoint opens onto its delivery log — what was sent, what
 * the receiver answered, when the next try is — and offers a test that is sent
 * at once. An endpoint that keeps failing is switched off by the Worker, and
 * says so here with the reason.
 *
 * Streaming is Enterprise's: elsewhere the page draws `EnterpriseOnly` in place
 * of its controls, and lists what an organization that dropped to Team still
 * has — delivering nothing — to remove.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import {
  STREAM_DISABLE_AFTER,
  type StreamCreated,
  type StreamDelivery,
  type StreamEndpoint,
  type StreamList
} from '@airlock/contracts/runs/streams';
import { color, font } from '../../theme/tokens';
import { Callout, Empty, Row, RowAction, StatusCell, Table } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { MenuButton } from '../../components/MenuButton';
import { EnterpriseOnly } from '../../components/EnterpriseBadge';
import { api, apiPath, prefix, type ApiPath } from '../../lib/api';
import type { Organization } from '../../lib/types';
import { SkeletonRows } from '../../components/Loading';
import { failureText, stamp } from './shared';
import { AddStream, KIND_WORDS, SecretShown, type StreamRequest } from './stream-parts';
import { useMotionMount } from '../../lib/presence';

export function StreamsPage({ organization }: { organization: Organization | null }): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const [list, setList] = useState<StreamList | null>(null);
  const [secret, setSecret] = useState<{ id: string; host: string; value: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The endpoint just added, whose row enters with the reveal (§42.8).
  const [fresh, setFresh] = useState<string | null>(null);
  const reveal = useMotionMount<HTMLDivElement>({ o: 0, y: 28 }, { d: 1, r: 0.55 });
  const [error, setError] = useState<string | null>(null);

  const base = organization ? apiPath`/orgs/${organization.id}/streams` : apiPath``;
  const reload = (): void => {
    if (!organization) return;
    api
      .get<StreamList>(base)
      .then(setList)
      .catch((failure: unknown) => setError(failureText(failure, 'The endpoints could not be read.')));
  };
  useEffect(reload, [organization?.id]);

  if (!organization) return <Empty title="Streaming belongs to an organization">A personal account has no audit to stream.</Empty>;

  const act = async (run: () => Promise<unknown>, fallback: string): Promise<void> => {
    setError(null);
    try {
      await run();
      reload();
    } catch (failure) {
      setError(failureText(failure, fallback));
    }
  };

  // Not Enterprise (the Worker's answer, `allowed`): the upsell in place of the
  // controls, and the endpoints an organization that dropped to Team still has,
  // delivering nothing, with Remove as their one verb.
  if (list && !list.allowed) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <EnterpriseOnly organization={organization} feature="Streaming the audit and the runs">
          {null}
        </EnterpriseOnly>
        {list.streams.length > 0 && (
          <Table columns="minmax(0,1fr) 120px" header={['Endpoint — nothing is sent while this organization is not on Enterprise', '']}>
            {list.streams.map((stream) => (
              <Row key={stream.id} columns="minmax(0,1fr) 120px">
                <span style={{ fontFamily: font.mono, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>{stream.hint}</span>
                <RowAction
                  danger
                  onClick={async () => {
                    const agreed = await ask({
                      title: `Remove ${stream.hint}?`,
                      lede: 'Nothing is streamed to it any more, and its delivery log goes with it. The audit and the runs stay.',
                      confirm: 'Remove',
                      danger: true
                    });
                    if (agreed) void act(() => api.del(apiPath`${prefix(base)}/${stream.id}`), 'That endpoint was not removed.');
                  }}
                >
                  Remove
                </RowAction>
              </Row>
            ))}
          </Table>
        )}
        {error && <p style={{ fontSize: 13.5, color: color.badText, margin: 0 }}>{error}</p>}
      </div>
    );
  }

  // A refusal is said under the address, in the add row (§42.8), not at the
  // foot of the page.
  const add = async (request: StreamRequest): Promise<string | null> => {
    try {
      const created = await api.post<StreamCreated>(base, request);
      setSecret({ id: created.stream.id, host: created.stream.host, value: created.secret });
      setFresh(created.stream.id);
      reload();
      return null;
    } catch (failure) {
      return failureText(failure, 'That endpoint was not added.');
    }
  };
  const columns = 'minmax(0,1.6fr) 110px 90px minmax(0,1.2fr) minmax(0,1.6fr)';

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {dialog}
      {list && !list.configured && (
        <Callout tone="amber" title="Streaming is not configured on this deployment">
          Its operator sets STREAMS_SIGNING_KEY, the key every endpoint’s signing secret is derived from. Until then nothing can be added or sent.
        </Callout>
      )}

      {secret && <SecretShown host={secret.host} value={secret.value} onDone={() => setSecret(null)} />}

      <Table
        columns={columns}
        header={['Endpoint', 'Sends', 'Format', 'State', '']}
        toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Endpoints</div>}
      >
        {(list?.streams ?? []).map((stream) => (
          <div
            key={stream.id}
            ref={stream.id === fresh ? reveal : undefined}
            className={stream.id === fresh ? 'al-line-lit' : undefined}
          >
            <Row columns={columns}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: font.mono, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>{stream.hint}</span>
                <span style={{ fontSize: 12, color: color.inkFaint }}>
                  {stream.lastAttemptAt ? `Last tried ${stamp(stream.lastAttemptAt)} · ${stream.lastStatus ?? 'unreachable'}` : 'Nothing sent yet'}
                </span>
              </span>
              <span style={{ fontSize: 13 }}>{stream.kinds.map((kind) => KIND_WORDS[kind]).join(' · ')}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12 }}>{stream.format}</span>
              <StreamState stream={stream} />
              {/* At most one text action, the rest in « More actions », the
                  destructive one last and red (redesign §41.3, proposal 1). */}
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                <RowAction onClick={() => setOpen(open === stream.id ? null : stream.id)}>{open === stream.id ? 'Hide log' : 'Log'}</RowAction>
                <MenuButton
                  icon
                  align="right"
                  label="More actions"
                  items={[
                    {
                      label: 'Send a test',
                      onPick: () => void act(() => api.post(apiPath`${prefix(base)}/${stream.id}/test`), 'The test was not sent.').then(() => setOpen(stream.id))
                    },
                    {
                      label: stream.enabled ? 'Switch off' : 'Switch on',
                      onPick: () => void act(() => api.patch(apiPath`${prefix(base)}/${stream.id}`, { enabled: !stream.enabled }), 'That did not change.')
                    },
                    {
                      label: 'New secret',
                      onPick: async () => {
                        // The old secret stops at once: asked first (proposal 19).
                        const agreed = await ask({
                          title: `Give ${stream.hint} a new secret?`,
                          lede: 'Deliveries are signed with the new one from now on, and the receiver refuses them until it has it. The endpoint, what it sends and its delivery log stay.',
                          confirm: 'New secret',
                          danger: true
                        });
                        if (!agreed) return;
                        void act(async () => {
                          const rotated = await api.post<StreamCreated>(apiPath`${prefix(base)}/${stream.id}/rotate`);
                          setSecret({ id: rotated.stream.id, host: rotated.stream.host, value: rotated.secret });
                        }, 'The secret was not changed.');
                      }
                    },
                    {
                      label: 'Remove',
                      danger: true,
                      separated: true,
                      onPick: async () => {
                        const agreed = await ask({
                          title: `Remove ${stream.hint}?`,
                          lede: 'Nothing is streamed to it any more, and its delivery log goes with it. The audit and the runs stay.',
                          confirm: 'Remove',
                          danger: true
                        });
                        if (!agreed) return;
                        void act(async () => {
                          await api.del(apiPath`${prefix(base)}/${stream.id}`);
                          // Its secret, if it is the one shown, goes with it: the
                          // note would otherwise name an endpoint no longer listed.
                          setSecret((shown) => (shown?.id === stream.id ? null : shown));
                        }, 'That endpoint was not removed.');
                      }
                    }
                  ]}
                />
              </span>
            </Row>
            {open === stream.id && <DeliveryLog key={stream.lastAttemptAt ?? stream.id} base={apiPath`${prefix(base)}/${stream.id}`} />}
          </div>
        ))}
      </Table>
      {list && list.streams.length === 0 && (
        <div style={{ margin: '-8px 0 0', fontSize: 13, color: color.inkSubtle }}>
          <div style={{ fontWeight: 600, color: color.ink }}>No endpoint yet</div>
          Add one below: the audit, the runs or both are sent to it as they happen.
        </div>
      )}
      <AddStream
        unavailable={list?.configured === false ? 'Streaming is not configured on this deployment' : null}
        onAdd={add}
      />
      <p style={{ margin: 0, fontSize: 12.5, color: color.inkSubtle, lineHeight: 1.55 }}>
        A delivery that fails is tried again after 1, 5 and 30 minutes, then 2 and 6 hours. An endpoint that fails {STREAM_DISABLE_AFTER} times in a row is switched
        off and the owners and administrators are mailed; what waited goes out when it is switched back on.
      </p>
      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: 0 }}>{error}</p>}
    </div>
  );
}

function StreamState({ stream }: { stream: StreamEndpoint }): React.JSX.Element {
  if (!stream.enabled) {
    return (
      <span style={{ display: 'grid', gap: 3 }}>
        <StatusCell tone={stream.disabledReason?.includes('failed') ? 'bad' : 'off'} ring={!stream.disabledReason?.includes('failed')} word="Off" />
        {stream.disabledReason && <span style={{ fontSize: 12, color: color.inkSubtle }}>{stream.disabledReason}</span>}
      </span>
    );
  }
  return stream.failures > 0 ? <StatusCell tone="warn" word="On" detail={`${stream.failures} failing`} /> : <StatusCell tone="ok" word="On" />;
}

const STATUS_TONE = { delivered: 'ok', pending: 'warn', failed: 'bad' } as const;

function DeliveryLog({ base }: { base: ApiPath }): React.JSX.Element {
  const [deliveries, setDeliveries] = useState<StreamDelivery[] | null>(null);
  useEffect(() => {
    api
      .get<{ deliveries: StreamDelivery[] }>(apiPath`${prefix(base)}/deliveries`)
      .then((answer) => setDeliveries(answer.deliveries))
      .catch(() => setDeliveries([]));
  }, [base]);
  return (
    <div style={{ background: color.surfaceAlt, borderBottom: `1px solid ${color.line}`, padding: '10px 22px 14px' }}>
      {deliveries === null ? (
        <SkeletonRows rows={2} columns="130px 70px 100px minmax(0,1fr)" />
      ) : deliveries.length === 0 ? (
        <span style={{ fontSize: 13, color: color.inkFaint }}>Nothing sent to it yet.</span>
      ) : (
        deliveries.map((delivery) => (
          <div key={delivery.id} style={{ display: 'grid', gridTemplateColumns: '130px 70px 100px minmax(0,1fr)', gap: 12, alignItems: 'center', padding: '5px 0', fontSize: 12.5 }}>
            <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }}>{stamp(delivery.createdAt)}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12 }}>{delivery.type}</span>
            <StatusCell tone={STATUS_TONE[delivery.status]} word={delivery.status} />
            <span style={{ color: color.inkSubtle, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {delivery.attempts} attempt{delivery.attempts === 1 ? '' : 's'}
              {delivery.lastStatus !== null ? ` · ${delivery.lastStatus}` : ''}
              {delivery.lastError ? ` · ${delivery.lastError}` : ''}
              {delivery.nextAt ? ` · next ${stamp(delivery.nextAt)}` : ''}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
